import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { MdExpandMore, MdChatBubbleOutline, MdGridView } from 'react-icons/md';
import { Spinner } from '@aibuild/ui';
import MenuListItem from '@components/1-atoms/MenuListItem';
import Field from '@components/1-atoms/Field';
import Dialog from '@components/2-molecules/Dialog';
import PageHeader, { PAGE_HEADER_VARIANT_MEDIUM } from '@components/2-molecules/PageHeader';
import useConversations from '@contexts/useConversations';
import { useActiveConversations } from '@hooks/useActiveConversations';
import { useSidebar } from '@contexts/useSidebar';
import { ConversationSummary } from '@contexts/ConversationsContextStore';
import {
  Container,
  Header,
  HeaderTitle,
  HeaderChevron,
  ListContainer,
  VirtualListInner,
  VirtualRow,
  ConversationItemWrapper,
  ConversationItem,
  ConversationReactIcon,
  ConversationTitle,
  SrOnly,
  RouterLink,
  MenuButton,
  MenuOverlay,
  Menu,
  EmptyState,
  EmptyText,
  LoadingContainer,
  LoadingMore,
} from './ConversationList.styled';

// Matches the nav item height in NavigationDrawer.styled.
const ITEM_HEIGHT = 42;

const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
  for (let node = element?.parentElement; node; node = node.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
};

// ── Individual list item ──────────────────────────────────────────────

const rowIcon = (isRunning: boolean, isAppRun: boolean) => {
  if (isRunning) return <Spinner size="md" dataTestId="conversation-running" />;
  return isAppRun ? <MdGridView size={22} /> : <MdChatBubbleOutline size={22} />;
};

interface ConversationListItemProps {
  conversation: ConversationSummary;
  isRunning: boolean;
  onRequestRename: (conversation: ConversationSummary) => void;
  onRequestDelete: (conversation: ConversationSummary) => void;
}

const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isRunning,
  onRequestRename,
  onRequestDelete,
}) => {
  const intl = useIntl();
  const { closeMobile } = useSidebar();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAbove, setMenuAbove] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleMenuClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!menuOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const minSpace = 120;
      setMenuAbove(spaceBelow < minSpace && spaceAbove > spaceBelow);
    }

    setMenuOpen((prev) => !prev);
  }, [menuOpen]);

  const handleRename = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(false);
      onRequestRename(conversation);
    },
    [conversation, onRequestRename],
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMenuOpen(false);
      onRequestDelete(conversation);
    },
    [conversation, onRequestDelete],
  );

  const handleOverlayClick = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const displayTitle = conversation.title || intl.formatMessage({
    id: 'conversations.newConversation',
    defaultMessage: 'New conversation',
  });

  const { appRun } = conversation;

  return (
    <ConversationItemWrapper ref={wrapperRef} data-menu-open={menuOpen ? 'true' : undefined}>
      <ConversationItem
        as={RouterLink}
        to={
          appRun
            ? `/apps/${encodeURIComponent(appRun.appKey)}/runs/${encodeURIComponent(appRun.runId)}`
            : `/conversation/${conversation.conversationId}`
        }
        onClick={closeMobile}
      >
        <ConversationReactIcon aria-hidden>
          {rowIcon(isRunning, Boolean(appRun))}
        </ConversationReactIcon>
        <ConversationTitle title={displayTitle}>{displayTitle}</ConversationTitle>
        {isRunning && (
          <SrOnly>
            {intl.formatMessage({ id: 'conversations.running.label', defaultMessage: 'Working' })}
          </SrOnly>
        )}
      </ConversationItem>
      <MenuButton
        iconName="more_vert_0"
        onClick={handleMenuClick}
        title={intl.formatMessage({ id: 'conversations.moreOptions', defaultMessage: 'More options' })}
      />
      {menuOpen && (
        <>
          <MenuOverlay onClick={handleOverlayClick} />
          <Menu ref={menuRef} $openAbove={menuAbove}>
            <MenuListItem
              label={intl.formatMessage({ id: 'conversations.rename.label', defaultMessage: 'Rename' })}
              leadingIconName="edit_0"
              onClick={handleRename}
              dataTestId="conversation-menu-rename"
            />
            <MenuListItem
              label={intl.formatMessage({ id: 'conversations.delete.label', defaultMessage: 'Delete' })}
              leadingIconName="delete_0"
              color="error"
              onClick={handleDelete}
              dataTestId="conversation-menu-delete"
            />
          </Menu>
        </>
      )}
    </ConversationItemWrapper>
  );
};

// ── Main list ─────────────────────────────────────────────────────────

const ConversationList: React.FC = () => {
  const intl = useIntl();
  const {
    conversations,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    renameConversation,
    deleteConversation,
  } = useConversations();
  // One poll for the whole list — the virtualised rows must not each open one.
  const runningConversationIds = useActiveConversations();
  const navigate = useNavigate();
  const location = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);

  // Rename dialog state – separate open flag so data persists during close animation
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameDialogConversation, setRenameDialogConversation] =
    useState<ConversationSummary | null>(null);
  const [renameDialogValue, setRenameDialogValue] = useState('');
  const [renameDialogLoading, setRenameDialogLoading] = useState(false);

  // Delete dialog state – separate open flag so data persists during close animation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogConversation, setDeleteDialogConversation] =
    useState<ConversationSummary | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const listInnerRef = useRef<HTMLDivElement>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  const virtualizer = useVirtualizer({
    count: conversations.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 10,
    scrollMargin,
  });

  // The virtualizer skips re-rendering when its visible range is unchanged, so a resize across the
  // single-scroll breakpoint would otherwise never re-run the scroller check below.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    window.addEventListener('resize', rerender);
    return () => window.removeEventListener('resize', rerender);
  }, []);

  // The list scrolls itself unless CSS hands scrolling to the drawer (short viewports). Re-checked every
  // render because the content above the list can change. Both setters bail out when nothing moved.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const list = scrollContainerRef.current;
    const inner = listInnerRef.current;
    const scroller =
      list && getComputedStyle(list).overflowY === 'visible' ? findScrollParent(list) : list;
    const margin =
      scroller && inner && scroller !== list
        ? inner.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop
        : 0;
    setScrollElement(scroller);
    setScrollMargin((prev) => (Math.abs(prev - margin) < 1 ? prev : margin));
  });

  // Infinite scroll – fetch next page when the user scrolls near the bottom
  useEffect(() => {
    const container = scrollElement;
    if (!container || !hasNextPage || isFetchingNextPage) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [scrollElement, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleRenameRequest = useCallback((conversation: ConversationSummary) => {
    setRenameDialogConversation(conversation);
    setRenameDialogValue(conversation.title || '');
    setRenameDialogOpen(true);
  }, []);

  const handleRename = useCallback(async () => {
    if (!renameDialogConversation || !renameDialogValue.trim()) return;
    setRenameDialogLoading(true);
    try {
      await renameConversation(renameDialogConversation.conversationId, renameDialogValue.trim());
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    } finally {
      setRenameDialogLoading(false);
      setRenameDialogOpen(false);
    }
  }, [renameConversation, renameDialogConversation, renameDialogValue]);

  const handleCloseRenameDialog = useCallback(() => {
    if (!renameDialogLoading) {
      setRenameDialogOpen(false);
    }
  }, [renameDialogLoading]);

  const handleDeleteRequest = useCallback((conversation: ConversationSummary) => {
    setDeleteDialogConversation(conversation);
    setDeleteDialogOpen(true);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteDialogConversation) return;
    const conversationId = deleteDialogConversation.conversationId;
    // An app run is open at its run id, which is not its conversation id, so
    // both have to be checked or deleting the page you are on leaves you there.
    const runId = deleteDialogConversation.appRun?.runId;
    setDeleteDialogLoading(true);
    try {
      await deleteConversation(conversationId);
      const openHere =
        location.pathname.includes(conversationId) ||
        (runId !== undefined && location.pathname.includes(runId));
      if (openHere) {
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    } finally {
      setDeleteDialogLoading(false);
      setDeleteDialogOpen(false);
    }
  }, [deleteConversation, deleteDialogConversation, location.pathname, navigate]);

  const handleCloseDeleteDialog = useCallback(() => {
    if (!deleteDialogLoading) {
      setDeleteDialogOpen(false);
    }
  }, [deleteDialogLoading]);

  const renameDialogHeader = useMemo(
    () =>
      intl.formatMessage({
        id: 'conversation.rename.title',
        defaultMessage: 'Rename conversation',
      }),
    [intl],
  );

  const deleteDialogTitle = useMemo(() => {
    if (!deleteDialogConversation?.title) {
      return intl.formatMessage({
        id: 'conversation.delete.untitled',
        defaultMessage: 'New conversation',
      });
    }
    return deleteDialogConversation.title;
  }, [deleteDialogConversation, intl]);

  const deleteDialogHeader = useMemo(
    () =>
      intl.formatMessage({
        id: 'conversation.delete.title',
        defaultMessage: 'Delete conversation?',
      }),
    [intl],
  );

  // An app run's conversation takes the run and its result files with it, which
  // is a bigger loss than a chat and has to be said before the user confirms.
  const deleteDialogSubtitle = useMemo(
    () =>
      deleteDialogConversation?.appRun
        ? intl.formatMessage(
            {
              id: 'conversation.delete.subtitle.appRun',
              defaultMessage:
                'This will permanently delete the {appName} run "{title}", its conversation, and every result file it produced.',
            },
            {
              title: deleteDialogTitle,
              appName: intl.formatMessage({
                id: deleteDialogConversation.appRun.nameKey,
                defaultMessage: deleteDialogConversation.appRun.nameDefault,
              }),
            },
          )
        : intl.formatMessage(
            {
              id: 'conversation.delete.subtitle',
              defaultMessage: 'This will permanently delete "{title}".',
            },
            { title: deleteDialogTitle },
          ),
    [deleteDialogConversation, deleteDialogTitle, intl],
  );

  const chatsTitle = intl.formatMessage({ id: 'conversations.title', defaultMessage: 'Tasks' });

  // Counted from the poll, not from the rendered rows: the list is virtualised
  // and paged, so a row-derived count would move as the user scrolls.
  const runningCount = runningConversationIds.size;
  const runningAnnouncement =
    runningCount === 0
      ? ''
      : intl.formatMessage(
          {
            id: 'conversations.running.count',
            defaultMessage: '{count, plural, one {# task working} other {# tasks working}}',
          },
          { count: runningCount },
        );

  const isInitialLoading = isLoading && conversations.length === 0;

  return (
    <>
      <Container>
        <Header type="button" onClick={handleToggleCollapse}>
          <HeaderTitle>{chatsTitle}</HeaderTitle>
          <HeaderChevron $isCollapsed={isCollapsed}>
            <MdExpandMore size={16} />
          </HeaderChevron>
        </Header>

        {/* Mounted from the first render, ahead of the list itself: a live
            region only announces changes made after it is in the DOM. */}
        <SrOnly role="status" aria-live="polite">
          {runningAnnouncement}
        </SrOnly>

        {isInitialLoading && (
          <LoadingContainer>
            <EmptyText>
              {intl.formatMessage({ id: 'conversations.loading', defaultMessage: 'Loading...' })}
            </EmptyText>
          </LoadingContainer>
        )}

        {!isInitialLoading && !isCollapsed && (
          <>
            {conversations.length === 0 ? (
              <EmptyState>
                <EmptyText>
                  {intl.formatMessage({ id: 'conversations.empty', defaultMessage: 'No conversations yet' })}
                </EmptyText>
              </EmptyState>
            ) : (
              <ListContainer ref={scrollContainerRef}>
                <VirtualListInner ref={listInnerRef} $height={virtualizer.getTotalSize()}>
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const conversation = conversations[virtualItem.index];
                    return (
                      <VirtualRow
                        key={conversation.conversationId}
                        $height={virtualItem.size}
                        $offset={virtualItem.start - scrollMargin}
                      >
                        <ConversationListItem
                          conversation={conversation}
                          isRunning={runningConversationIds.has(conversation.conversationId)}
                          onRequestRename={handleRenameRequest}
                          onRequestDelete={handleDeleteRequest}
                        />
                      </VirtualRow>
                    );
                  })}
                </VirtualListInner>

                {isFetchingNextPage && (
                  <LoadingMore>
                    {intl.formatMessage({ id: 'conversations.loadingMore', defaultMessage: 'Loading more...' })}
                  </LoadingMore>
                )}
              </ListContainer>
            )}
          </>
        )}
      </Container>

      <Dialog
        closeOnClickOutside
        closeOnEscape
        onClose={handleCloseRenameDialog}
        onCloseIconButtonClick={handleCloseRenameDialog}
        onPrimaryButtonClick={handleRename}
        onSecondaryButtonClick={handleCloseRenameDialog}
        primaryButtonLabel={intl.formatMessage({
          id: 'conversation.rename.confirm',
          defaultMessage: 'Rename',
        })}
        secondaryButtonLabel={intl.formatMessage({
          id: 'conversation.rename.cancel',
          defaultMessage: 'Cancel',
        })}
        primaryButtonLoading={renameDialogLoading}
        primaryButtonDisabled={renameDialogLoading || !renameDialogValue.trim()}
        secondaryButtonDisabled={renameDialogLoading}
        show={renameDialogOpen}
      >
        <PageHeader
          variant={PAGE_HEADER_VARIANT_MEDIUM}
          title={renameDialogHeader}
        />
        <Field
          autoFocus
          value={renameDialogValue}
          onChange={setRenameDialogValue}
          placeholder={intl.formatMessage({
            id: 'conversation.rename.placeholder',
            defaultMessage: 'Enter a new name',
          })}
          passFieldValueOnChange
          dataTestId="rename-field"
        />
      </Dialog>

      <Dialog
        closeOnClickOutside
        closeOnEscape
        onClose={handleCloseDeleteDialog}
        onCloseIconButtonClick={handleCloseDeleteDialog}
        onPrimaryButtonClick={handleDelete}
        onSecondaryButtonClick={handleCloseDeleteDialog}
        primaryButtonLabel={intl.formatMessage({
          id: 'conversation.delete.confirm',
          defaultMessage: 'Delete',
        })}
        secondaryButtonLabel={intl.formatMessage({
          id: 'conversation.delete.cancel',
          defaultMessage: 'Cancel',
        })}
        primaryButtonLoading={deleteDialogLoading}
        primaryButtonDisabled={deleteDialogLoading}
        secondaryButtonDisabled={deleteDialogLoading}
        show={deleteDialogOpen}
      >
        <PageHeader
          variant={PAGE_HEADER_VARIANT_MEDIUM}
          title={deleteDialogHeader}
          subtitle={deleteDialogSubtitle}
        />
      </Dialog>
    </>
  );
};

export default ConversationList;
